// Tarjeta de un equipo en «Elige el teléfono»: producto con su existencia, crédito y la fila de promos propias.
// El crédito solo se pinta con SureKeep; la grilla iguala el alto de las tarjetas (DEC-47).
import { Check, Package, Smartphone } from 'lucide-react';
import { cn } from '@/lib/cn';
import { itemDisplayName } from '@/shared/lib/format/itemDisplayName';
import type { LevelCreditTerms } from '../cardBenefits';
import { itemLabelOf } from '../cartLines';
import { isSoldOut } from '../catalogAvailability';
import { kitComponentCount } from '../kitContents';
import type { BenefitBubble, CardSaleBenefits } from '../promotionSlots';
import type { CatalogItem } from '../types';
import { CardCreditSection, CardPrice, CardPromosSection, cardPriceOf } from './CardPricing';
import { HighlightedText } from './HighlightedText';
import { StockChip } from './StockChip';
import { variantLabel } from '@/shared/lib/format/capacity';

const NO_SALE_BENEFITS: CardSaleBenefits = { bubbles: [], discountedPriceCents: null };
const NO_TERMS: LevelCreditTerms = {};

export function ProductCard({
    item,
    selected,
    terms,
    onSelect,
    saleBenefits = NO_SALE_BENEFITS,
    creditTerms = NO_TERMS,
    onShowKitContents,
    creditBubbles = [],
}: {
    item: CatalogItem;
    selected: boolean;
    /** Términos de la búsqueda, para resaltar la coincidencia. */
    terms: string[];
    onSelect: (sku: string) => void;
    /** Burbujas propias y precio con descuento que pone Promociones. */
    saleBenefits?: CardSaleBenefits;
    /** % del plan y tope del nivel, para el enganche desde. */
    creditTerms?: LevelCreditTerms;
    /** «Ver qué incluye» de un kit; sin handler no se muestra. */
    onShowKitContents?: (sku: string) => void;
    /** Burbujas moradas de las promos de crédito propias del equipo; quien monta decide si van. */
    creditBubbles?: readonly BenefitBubble[];
}) {
    const soldOut = isSoldOut(item);
    const variant = variantLabel(item);
    const { priceCents } = cardPriceOf(item.cashPriceCents, saleBenefits);
    const isKit = item.category === 'BUNDLE';
    const name = item.model !== '' ? item.model : itemDisplayName(item);
    const KindIcon = isKit ? Package : Smartphone;
    const showKitLink = isKit && onShowKitContents !== undefined;
    const bubbles = [...saleBenefits.bubbles, ...creditBubbles];

    const card = (
        <button
            type="button"
            disabled={soldOut}
            aria-pressed={selected}
            onClick={() => {
                onSelect(item.sku);
            }}
            className={cn(
                'relative flex min-w-0 flex-1 flex-col overflow-hidden rounded-2xl border bg-card text-left transition-all',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1',
                selected
                    ? 'border-foreground shadow-[0_0_0_1px_hsl(var(--foreground))]'
                    : 'border-border hover:border-muted-foreground',
                soldOut && 'cursor-not-allowed opacity-[0.52] hover:border-border',
                showKitLink && 'col-start-1 row-span-3 row-start-1 grid grid-rows-subgrid',
            )}
        >
            <span
                data-section="product"
                className="flex min-w-0 flex-1 flex-col gap-0.5 px-4 pb-3.5 pt-4"
            >
                <span className="flex min-h-5 items-center gap-1.5 pr-6 text-muted-foreground">
                    <KindIcon className="size-4 flex-none" aria-hidden />
                    <span className="min-w-0 truncate text-[11px] font-bold uppercase tracking-[0.06em]">
                        {item.brand !== '' && (
                            <>
                                <HighlightedText text={item.brand} terms={terms} /> ·{' '}
                            </>
                        )}
                        {itemLabelOf(item)}
                    </span>
                </span>
                {selected && (
                    <span className="absolute right-3 top-3 flex size-[22px] items-center justify-center rounded-full bg-primary text-primary-foreground">
                        <Check className="size-3" aria-hidden />
                    </span>
                )}
                <span className="mt-1 flex min-w-0 items-center gap-1.5">
                    <span
                        data-testid="card-name"
                        title={name}
                        className="min-w-0 truncate text-[16px] font-bold leading-tight"
                    >
                        <HighlightedText text={name} terms={terms} />
                    </span>
                    <StockChip item={item} className="flex-none whitespace-nowrap" />
                </span>
                {/* Capacidad y color distinguen dos variantes del mismo modelo, con precios distintos. */}
                <span className="min-h-[18px] truncate text-[12px] text-muted-foreground">
                    {variant !== '' && <HighlightedText text={variant} terms={terms} />}
                </span>
                <CardPrice listCents={item.cashPriceCents} saleBenefits={saleBenefits} />
                {/* Hueco al fondo del bloque para el enlace del kit, que va encima (no se anidan botones). */}
                {showKitLink && <span aria-hidden className="mt-auto block h-5 flex-none" />}
            </span>

            <CardCreditSection item={item} priceCents={priceCents} creditTerms={creditTerms} />
            <CardPromosSection bubbles={bubbles} />
        </button>
    );
    if (!isKit || onShowKitContents === undefined) return card;

    return (
        // Grilla compartida con la tarjeta: el enlace cae al fondo de la fila del producto, en su hueco.
        // La columna en minmax(0,1fr) evita que un nombre largo ensanche la tarjeta del kit.
        <div className="grid min-w-0 flex-1 grid-cols-[minmax(0,1fr)] grid-rows-[1fr_auto_auto]">
            {card}
            <button
                type="button"
                onClick={() => {
                    onShowKitContents(item.sku);
                }}
                className="relative col-start-1 row-start-1 mb-3.5 ml-4 h-5 self-end justify-self-start rounded-sm text-[12px] font-semibold text-primary underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
                Ver qué incluye ({kitComponentCount(item)})
            </button>
        </div>
    );
}

/** Placeholder con la MISMA silueta de la tarjeta — la grilla no salta cuando llegan los datos. */
export function ProductCardSkeleton() {
    return (
        <div className="flex animate-pulse flex-col overflow-hidden rounded-2xl border border-border bg-card">
            <div className="flex flex-col gap-2 px-4 pb-3.5 pt-4">
                <div className="h-3 w-20 rounded bg-muted" />
                <div className="h-4 w-28 rounded bg-muted" />
                <div className="h-3 w-16 rounded bg-muted" />
                <div className="mt-4 h-6 w-24 rounded bg-muted" />
            </div>
            <div className="h-[60px] border-t border-border/60" />
            <div className="h-[45px]" />
        </div>
    );
}
